import { Bathymetry, type MapMeta } from '@core/map/bathymetry';

// Die Kartendaten liegen unter data/ und werden von tools/build_map.py erzeugt.
// `?url` sorgt dafuer, dass Vite die Dateien als eigene Assets ausliefert,
// statt sie ins JavaScript-Bundle einzubetten — die Kuestenlinien allein waeren
// sonst 160 kB im Hauptchunk.
import bathymetryUrl from '../../data/map/bathymetry.png';
import metaUrl from '../../data/map/map.meta.json?url';
import coastlineUrl from '../../data/map/coastline.json?url';

/** Ein zusammenhaengender Kuestenzug als Folge von [lon, lat]-Paaren. */
export type CoastlineStrand = readonly (readonly [number, number])[];

export interface MapData {
  readonly bathymetry: Bathymetry;
  readonly coastline: readonly CoastlineStrand[];
}

export type ProgressCallback = (fraction: number, label: string) => void;

/** Laedt ein Bild und liefert seine Bildpunkte als RGBA. */
async function loadPixels(url: string): Promise<{
  pixels: Uint8ClampedArray;
  width: number;
  height: number;
}> {
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  await image.decode();

  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;

  // willReadFrequently, weil wir genau einmal alles auslesen und danach
  // den Canvas verwerfen — der Browser soll dafuer nicht auf die GPU gehen.
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (context === null) {
    throw new Error('Kartendaten: 2D-Kontext nicht verfügbar');
  }

  context.drawImage(image, 0, 0);
  const data = context.getImageData(0, 0, canvas.width, canvas.height);

  return { pixels: data.data, width: canvas.width, height: canvas.height };
}

export async function loadMapData(onProgress: ProgressCallback = () => {}): Promise<MapData> {
  onProgress(0.05, 'meta');
  const meta = (await fetch(metaUrl).then((r) => r.json())) as MapMeta;

  onProgress(0.2, 'bathymetry');
  const { pixels, width, height } = await loadPixels(bathymetryUrl);

  if (width !== meta.grid.width || height !== meta.grid.height) {
    throw new Error(
      `Kartendaten: Bild ist ${width}x${height}, laut map.meta.json aber ${meta.grid.width}x${meta.grid.height}`,
    );
  }

  onProgress(0.7, 'decode');
  const bathymetry = Bathymetry.fromEncodedRGBA(pixels, meta);

  onProgress(0.85, 'coastline');
  const coast = (await fetch(coastlineUrl).then((r) => r.json())) as {
    lines: [number, number][][];
  };

  onProgress(1, 'done');
  return { bathymetry, coastline: coast.lines };
}
